import { ComponentIds, ConnectorsComponent, HierarchyUtils, IsDraggedComponent, HostConnectionsComponent, TransformUtils, Vector, ViewerDragEventComponent, ViewerObjectComponent, SnapUtils, } from "@inter-ikea-core/core";
import { getRootMountingRail, onClickInvisibleZone } from "./MountingRails";
import { NO_UPRIGHTS } from "../constants/errors";
import { COMPONENT_IDS } from "@inter-ikea-storagetwo/range/dist/storage2ComponentIDs";
import { getIpexViewer } from "@inter-ikea-core/ipex-betrakta-core-adapter";
import { MOUNTING_RAIL_RIGHT_SIDE_UPRIGHT_CONNECTORS, MOUNTING_RAIL_TO_UPRIGHT_LEFT, UPRIGHT_TO_MOUNTING_RAIL_LEFT, } from "../constants/connectorKeys";
import { getUprights, hasUprights } from "../utils/sectionsHelpers";
import { createViewerObject, viewerObjectRedo, } from "../utils/viewerObjectHandler";
import { getConnectorsComponent, getHostConnections, getTemplateDataComponent, } from "@inter-ikea-storagetwo/range/dist/functions/componentGetters";
import { CANT_CREATE_VIEWER_OBJECT, CONNECTOR_DOESNT_EXISTS, NO_PARENT, NO_VIEWER, } from "@inter-ikea-storagetwo/range/dist/constants/errors";
import { VIEWER_OBJECT_CONNECTOR } from "@inter-ikea-storagetwo/range/dist/constants/connectorKeys";
const viewerObjectName = "invisible-viewer-object";
/**
 * Checks if a connection is valid for setting up a clickable box
 *
 * @param connectorPosition
 */
export const validConnection = ({ hostConnector }) => [
    ...UPRIGHT_TO_MOUNTING_RAIL_LEFT,
    ...MOUNTING_RAIL_RIGHT_SIDE_UPRIGHT_CONNECTORS,
].includes(hostConnector.key);
/**
 * Fetches height of entity
 *
 * @param _entity
 */
export const getEntityHeight = (_entity) => getTemplateDataComponent(_entity).size.size;
/**
 * Creates a viewer object and sets it up
 *
 * @param core
 * @param standardSelectionService
 * @param child
 * @param parent
 * @param width
 * @param height
 */
export const modifyViewerObject = (core, parent, size) => {
    const viewerObject = createViewerObject(core, parent, size);
    /**
     * This fixes an issue where resizing sections would cause the drag to not work the first time
     * Should probably talk with Core why this isn't working
     * How to reproduce:
     *  - Create two sections that are connected to each other
     *  - Resize the section on the right
     *  - Then try to drag the sections
     */
    const viewer = getIpexViewer(core.services.sceneManager);
    if (!viewer)
        throw new Error(NO_VIEWER);
    viewer
        .get_object_drag_begin_observable(viewerObject.getViewerObjectId())
        .add(() => {
        const rootMountingRail = getRootMountingRail(parent);
        if (rootMountingRail.hasComponent(ComponentIds.IS_DRAGGED_COMPONENT_ID) &&
            rootMountingRail.hasComponent(ComponentIds.VIEWER_DRAG_EVENT_COMPONENT_ID)) {
            rootMountingRail.removeComponent(ComponentIds.IS_DRAGGED_COMPONENT_ID);
            rootMountingRail.addComponent(new IsDraggedComponent());
            const viewerDrag = rootMountingRail.getComponent(ComponentIds.VIEWER_DRAG_EVENT_COMPONENT_ID);
            if (!viewerDrag)
                return;
            rootMountingRail.removeComponent(ComponentIds.VIEWER_DRAG_EVENT_COMPONENT_ID);
            rootMountingRail.addComponent(new ViewerDragEventComponent(viewerDrag.planePoint, viewerDrag.planeNormal, viewerDrag.delta, viewerDrag.cameraPosition));
        }
    });
    return viewerObject;
};
/**
 * Create entity for holding viewer object
 * @param core
 * @param viewerObjectName
 */
export const createViewerObjectEntity = (core, viewerObjectName) => {
    const viewerObjectEntity = core.services.productManager.createEntity(viewerObjectName);
    if (!viewerObjectEntity)
        throw new Error(CANT_CREATE_VIEWER_OBJECT);
    return viewerObjectEntity;
};
/**
 * Creates nessesary viewer object related data
 *
 * @param core
 * @param standardSelectionService
 * @param mountingRail
 * @param width
 * @param height
 */
export const buildViewerObjectEntity = (core, mountingRail, width, height) => {
    const viewerObjectEntity = createViewerObjectEntity(core, viewerObjectName);
    const viewerObject = modifyViewerObject(core, mountingRail, new Vector(width, height, 1));
    viewerObject.makeClickable(onClickInvisibleZone(mountingRail));
    const connector = viewerObjectEntity.getComponentUnsafe(ComponentIds.CONNECTORS_COMPONENT_ID).connectors[0];
    const newConnector = connector.move(new Vector(-width / 2, height / 2, 0));
    viewerObjectEntity.addComponent(new ConnectorsComponent([newConnector]));
    viewerObjectEntity.addComponent(new ViewerObjectComponent(viewerObject));
    return viewerObjectEntity;
};
export const getRightUprightsOffsetFromLeft = (mountingRail, rightUpright) => {
    var _a;
    const leftPosition = (_a = getConnectorsComponent(mountingRail).getConnectorByKey(MOUNTING_RAIL_TO_UPRIGHT_LEFT)) === null || _a === void 0 ? void 0 : _a.transform.position.X;
    if (!leftPosition)
        throw new Error("No left position.");
    const rightUprightPosition = TransformUtils.localPosition(rightUpright).X;
    return rightUprightPosition - leftPosition;
};
const clearOldViewerObject = (connection) => {
    var _a;
    const hostConnections = getHostConnections(connection.hostConnectorEntity);
    const viewerEntity = (_a = hostConnections.find(({ hostConnector }) => hostConnector.key === VIEWER_OBJECT_CONNECTOR)) === null || _a === void 0 ? void 0 : _a.guestConnectorEntity;
    const filteredConnections = hostConnections.filter(({ hostConnector }) => hostConnector.key !== VIEWER_OBJECT_CONNECTOR);
    connection.hostConnectorEntity.addComponent(new HostConnectionsComponent(filteredConnections));
    viewerEntity === null || viewerEntity === void 0 ? void 0 : viewerEntity.dispose();
};
/**
 * Creates the viewer object entity and snaps it to the parent mounting rail
 * @param core
 * @param connection
 */
export const createAndSnapViewerObjectToMountingRail = (core, connection) => {
    var _a, _b;
    const { hostConnectorEntity, guestConnectorEntity } = connection;
    if ((_a = HierarchyUtils.parent(hostConnectorEntity)) === null || _a === void 0 ? void 0 : _a.hasComponent(COMPONENT_IDS.UPRIGHT_COMPONENT))
        hostConnectorEntity.removeComponent(ComponentIds.DRAGGABLE_COMPONENT_ID);
    if (!hasUprights(hostConnectorEntity) || !validConnection(connection))
        return;
    clearOldViewerObject(connection);
    const { leftUpright } = getUprights(hostConnectorEntity);
    if (!leftUpright)
        throw new Error(NO_UPRIGHTS);
    const sectionWidth = getRightUprightsOffsetFromLeft(hostConnectorEntity, guestConnectorEntity);
    const entityHeight = getEntityHeight(guestConnectorEntity);
    if (!entityHeight)
        throw new Error("ENTITY DIDNT HAVE A HEIGHT");
    const viewerObjectEntity = buildViewerObjectEntity(core, hostConnectorEntity, sectionWidth, entityHeight - 10);
    const guestConnector = getConnectorsComponent(viewerObjectEntity).getConnectorByKey(VIEWER_OBJECT_CONNECTOR);
    const hostConnector = getConnectorsComponent(hostConnectorEntity).getConnectorByKey(VIEWER_OBJECT_CONNECTOR);
    if (!hostConnector || !guestConnector)
        throw new Error(CONNECTOR_DOESNT_EXISTS);
    (_b = SnapUtils.getSnap(viewerObjectEntity, guestConnector, hostConnectorEntity, hostConnector)) === null || _b === void 0 ? void 0 : _b.performMoveAndConnect();
};
/**
 * On redo we already have a viewerObject entity, this function will only create and attach the viewerObject to the entity
 *
 * @param viewerObjectEntity
 * @param core
 * @param standardSelectionService
 */
export const handleSectionViewerObjectOnRedo = (viewerObjectEntity, core) => {
    const parent = HierarchyUtils.parent(viewerObjectEntity);
    if (!parent)
        throw new Error(NO_PARENT);
    const { rightUpright } = getUprights(parent);
    if (!rightUpright)
        throw new Error(NO_UPRIGHTS);
    const height = getEntityHeight(rightUpright) - 10;
    const width = getRightUprightsOffsetFromLeft(parent, rightUpright);
    const size = new Vector(width, height, 1);
    const viewerObject = viewerObjectRedo(viewerObjectEntity, core, size);
    viewerObject.makeClickable(onClickInvisibleZone(parent));
};
export const isBoaxelSectionRedo = (entity) => {
    var _a;
    return (_a = HierarchyUtils.parent(entity)) === null || _a === void 0 ? void 0 : _a.hasComponent(COMPONENT_IDS.MOUNTING_RAIL_COMPONENT);
};
